import { FC, ReactElement, ReactNode } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import Backdrop from "./Backdrop";
import Alert from "./Alert/Alert";
import AlertDialogs from "./AlertDialogs/AlertDialogs";
import ScanDialog from "./ScanDialogs/ScanDialog";
import Header, { HeaderProps } from "./Header/Header";
import Footer, { FooterProps } from "./Footer/Footer";
import { containerStyle, FOOTER_HEIGHT } from "./consts";
import ConnectionHealthAlert from "./ConnectionHealthAlert/ConnectionHealthAlert";

type MuiPageProps = HeaderProps & {
    variant: "default" | "flow5";
    /*
     * Optional footer component to the bottom of the page
     */
    footerContent?: ReactNode;
    /*
     * Set loading state of the page to display circular progress animation on dark background
     */
    loading?: boolean;
    /*
     * If it is true, mobile icon will be placed in title for marking the page is optimized for mobile too
     */
    responsive?: boolean;
    /*
     * Page alert message(s) to display
     */
    alert?: string | object | [];
    /**
     * Pass properties to the Footer component
     */
    footerProps?: FooterProps;
    children: ReactNode;
};

/**
 * Sets up common stlye configurtion and header component for pages, implementing a new MUI layout.
 * Handle variants: default (legacy style) and flow5 (new style for search forms)
 */
const MuiPage: FC<MuiPageProps> = ({
    variant = "default",
    headerProps,
    HeaderComponent,
    stickyHeader,
    footerContent,
    footerProps,
    loading = false,
    responsive = false,
    alert,
    children,
}): ReactElement => {
    return (
        <>
            {variant === "flow5" && <Backdrop open={loading} />}
            <AlertDialogs />
            <ScanDialog />
            <Box
                id="muiPage"
                sx={{
                    ...(containerStyle[variant] || containerStyle["default"]),
                    mb: footerContent ? FOOTER_HEIGHT : undefined,
                }}
            >
                <Stack direction="column" alignItems="stretch" spacing={3}>
                    <ConnectionHealthAlert />

                    {/* HEADER */}
                    <Header
                        stickyHeader={stickyHeader}
                        responsive={responsive}
                        HeaderComponent={HeaderComponent}
                        headerProps={headerProps}
                    />

                    {/* ALERT SECTION */}
                    {alert && <Alert alert={alert} />}

                    {/* CONTENT */}
                    <Box>{children}</Box>
                </Stack>

                {/* FOOTER */}
                {!!footerContent && (
                    <Footer {...footerProps}>{footerContent}</Footer>
                )}
            </Box>
        </>
    );
};

export default MuiPage;
export { default as ResponsivePageIcon } from "./PageHeader/ResponsiveIcon";
